import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Building2,
  Briefcase,
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink,
  Kanban,
  LayoutGrid,
  LayoutList,
  Mail,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
  AlertCircle,
  DollarSign,
  Sparkles,
  FileSpreadsheet,
  Inbox,
  HelpCircle,
  Archive,
  Filter,
  Check,
  BookOpen,
} from 'lucide-react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import GermanHero, {
  FlagBar,
  heroPrimaryBtn,
  heroGhostBtn,
  surfaceCard,
} from '../components/ui/GermanHero';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { CopyButton } from '../components/copy-button';
import { StatusIndicator } from '../components/indicator';
import { cn } from '../lib/utils';
import {
  JOB_STATUSES,
  JOB_PLATFORMS,
  canUseJobTracker,
  fetchJobApplications,
  createJobApplication,
  updateJobApplication,
  deleteJobApplication,
  updateJobStatus,
  getUserEmailShortcuts,
  exportApplicationsToExcel,
  getPlatformLabel,
} from '../lib/jobTracker';

const STATUS_MAP = Object.fromEntries(JOB_STATUSES.map((s) => [s.id, s]));

// German application vocabulary strip (Bewerbungswortschatz B2)
const BEWERBUNGS_GLOSSAR = [
  { de: 'das Anschreiben', sq: 'Letra e motivimit' },
  { de: 'der Lebenslauf', sq: 'CV / Biografia' },
  { de: 'die Eingangsbestätigung', sq: 'Konfirmimi i pranimit' },
  { de: 'das Vorstellungsgespräch', sq: 'Intervista e punës' },
  { de: 'die Gehaltsvorstellung', sq: 'Pritshmëria e pagës' },
  { de: 'die Zusage / das Angebot', sq: 'Pranimi / Oferta' },
];

const T = {
  sq: {
    eyebrow: 'KARRIERE & BEWERBUNG · B2',
    titleLine1: 'Bewerbungsmappe.',
    titleLine2: 'Aplikimet e punës.',
    subtitle:
      'Menaxho aplikimet e punës (StepStone, LinkedIn, XING, KosovaJob), ftesat për Vorstellungsgespräch dhe konfirmimet me email.',
    exportExcel: 'Shkarko Excel',
    newApp: 'Aplikim i ri (Bewerbung)',
    glossarTitle: 'Fjalori i Aplikimit (Bewerbungswortschatz)',
    searchPlaceholder: 'Kërko kompani (Unternehmen), pozitë (Stelle), titull emaili...',
    allStatuses: 'Të gjitha statuset',
    allPlatforms: 'Të gjitha platformat',
    clearFilters: 'Pastro filtrat',
    viewCards: 'Karta',
    viewList: 'Listë',
    viewKanban: 'Kanban',
    loading: 'Po ngarkohen aplikimet...',
    errorLoading: 'Nuk u arrit të ngarkohen aplikimet. Ju lutemi provoni përsëri.',
    retry: 'Provo përsëri',
    noAppsFound: 'Asnjë aplikim i gjetur (Keine Bewerbung)',
    noAppsDescFilter: 'Nuk ka aplikime që përputhen me filtrat aktivë.',
    noAppsDescEmpty:
      'Nuk keni regjistruar ende asnjë aplikim. Shtoni aplikimin tuaj të parë për të ndjekur procesin e punësimit.',
    addFirstApp: 'Shto aplikimin e parë',
    stats: {
      total: 'Gesamt · Gjithsej',
      totalSub: 'aplikime të regjistruara',
      active: 'Im Prozess · Në Proces',
      activeSub: 'aplikuar, test ose follow-up',
      noResponse: 'Offen · Pa Përgjigje',
      noResponseSub: 'në pritje të përgjigjes',
      interviews: 'Gespräche · Intervista',
      interviewsSub: 'Vorstellungsgespräche',
      offers: 'Angebote · Oferta',
      offersSub: 'Zusage / kontratë',
      closed: 'Beendet · Mbyllur',
      closedSub: 'Absage ose tërhequr',
    },
    card: {
      emailSubject: 'Betreff · Subjekti',
      sender: 'Absender',
      responseAccount: 'Postfach',
      noEmailLinked: 'Pa email të lidhur (Kein Betreff)',
      openLink: 'Hap shpalljen (Stellenanzeige)',
      edit: 'Ndrysho',
      delete: 'Fshij',
      emptyColumn: 'Asnjë aplikim në këtë fazë',
    },
    modal: {
      createTitle: 'Regjistro Aplikim të Ri (Neue Bewerbung)',
      editTitle: 'Ndrysho Aplikimin (Bewerbung bearbeiten)',
      subtitle: 'Ruaj të dhënat e kompanisë, pozitës dhe dëshminë e emailit në mënyrë të strukturuar.',
      secJob: '1. Unternehmen & Stelle · Kompania & Pozita',
      company: 'Unternehmen · Kompania',
      companyPlaceholder: 'p.sh. SAP SE / PRIOjet GmbH',
      role: 'Stelle / Position · Roli',
      rolePlaceholder: 'p.sh. Full Stack Entwickler (m/w/d)',
      status: 'Bewerbungsstatus · Statusi',
      platform: 'Plattform · Platforma',
      secEmail: '2. E-Mail-Nachweis · Verifikimi i Email-it',
      emailSubject: 'E-Mail-Betreff · Titulli i Email-it',
      emailSubjectPlaceholder: 'p.sh. Eingangsbestätigung Ihrer Bewerbung...',
      emailSubjectHint: 'Titulli i saktë i emailit për ta gjetur menjëherë në Inbox.',
      contactEmail: 'Ansprechpartner / Absender · Dërguesi',
      contactEmailPlaceholder: 'p.sh. karriere@firma.de',
      responseAccount: 'Empfangskonto · Llogaria ku u mor përgjigja',
      responseAccountHint: 'Zgjidh ose shkruaj adresën tënde të emailit ku erdhi përgjigja.',
      quickShortcuts: 'Llogaria juaj:',
      secTimeline: '3. Termine & Gehalt · Afatet & Paga',
      appliedOn: 'Beworben am · Data e Aplikimit',
      nextStepOn: 'Gesprächstermin · Data e Intervistës',
      salary: 'Gehalt · Paga / Oferta',
      salaryPlaceholder: 'p.sh. 55.000 € / vit',
      secDetails: '4. Stellenlink & Notizen · Vegëza & Shënime',
      link: 'Stellenanzeige (URL) · Vegëza e shpalljes',
      notes: 'Notizen · Shënime, pyetje për Vorstellungsgespräch',
      notesPlaceholder: 'Shëno personin kontaktues, pyetjet në gjermanisht, versionin e Lebenslauf...',
      cancel: 'Anulo',
      delete: 'Fshij',
      save: 'Ruaj Aplikimin',
      update: 'Përditëso',
      saving: 'Duke ruajtur...',
    },
    deleteModal: {
      title: 'Konfirmo Fshirjen (Löschen bestätigen)',
      message: 'A jeni të sigurt që dëshironi ta fshini këtë aplikim? Ky veprim nuk mund të kthehet pas.',
      cancel: 'Anulo',
      confirm: 'Fshij përgjithmonë',
    },
  },
  de: {
    eyebrow: 'KARRIERE & BEWERBUNG · B2',
    titleLine1: 'Bewerbungsmappe.',
    titleLine2: 'Karriere im Überblick.',
    subtitle:
      'Verfolge deine Bewerbungen, Eingangsbestätigungen, Vorstellungsgespräche und Vertragsangebote an einem Ort.',
    exportExcel: 'Excel exportieren',
    newApp: 'Neue Bewerbung',
    glossarTitle: 'Bewerbungswortschatz B2',
    searchPlaceholder: 'Unternehmen, Position, Betreff, E-Mail-Konto suchen...',
    allStatuses: 'Alle Status',
    allPlatforms: 'Alle Plattformen',
    clearFilters: 'Filter zurücksetzen',
    viewCards: 'Karten',
    viewList: 'Liste',
    viewKanban: 'Kanban',
    loading: 'Bewerbungen werden geladen...',
    errorLoading: 'Bewerbungen konnten nicht geladen werden. Bitte erneut versuchen.',
    retry: 'Erneut versuchen',
    noAppsFound: 'Keine Bewerbungen gefunden',
    noAppsDescFilter: 'Keine Bewerbungen entsprechen den ausgewählten Filtern.',
    noAppsDescEmpty:
      'Du hast noch keine Bewerbungen erfasst. Lege deine erste Bewerbung an, um deinen Prozess zu strukturieren.',
    addFirstApp: 'Erste Bewerbung anlegen',
    stats: {
      total: 'Gesamt',
      totalSub: 'erfasste Bewerbungen',
      active: 'Im Prozess',
      activeSub: 'beworben, Test oder Follow-up',
      noResponse: 'Offen',
      noResponseSub: 'ohne Rückmeldung',
      interviews: 'Gespräche',
      interviewsSub: 'Vorstellungsgespräche',
      offers: 'Angebote',
      offersSub: 'Zusagen erhalten',
      closed: 'Beendet',
      closedSub: 'Absage oder zurückgezogen',
    },
    card: {
      emailSubject: 'Betreff',
      sender: 'Absender',
      responseAccount: 'Postfach',
      noEmailLinked: 'Kein Betreff hinterlegt',
      openLink: 'Stellenanzeige öffnen',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      emptyColumn: 'Keine Bewerbungen in dieser Phase',
    },
    modal: {
      createTitle: 'Neue Bewerbung erfassen',
      editTitle: 'Bewerbung bearbeiten',
      subtitle: 'Pflege deine Unternehmens-, Positions- und E-Mail-Daten übersichtlich.',
      secJob: '1. Unternehmen & Position',
      company: 'Unternehmen',
      companyPlaceholder: 'z. B. SAP SE / PRIOjet GmbH',
      role: 'Position / Stelle',
      rolePlaceholder: 'z. B. Full Stack Entwickler (m/w/d)',
      status: 'Bewerbungsstatus',
      platform: 'Plattform',
      secEmail: '2. E-Mail-Nachweis & Empfangskonto',
      emailSubject: 'E-Mail-Betreff (Subject Line)',
      emailSubjectPlaceholder: 'z. B. Eingangsbestätigung Ihrer Bewerbung...',
      emailSubjectHint: 'Genauer Betreff, um die E-Mail im Posteingang sofort wiederzufinden.',
      contactEmail: 'Ansprechpartner / Absender',
      contactEmailPlaceholder: 'z. B. karriere@unternehmen.de',
      responseAccount: 'Empfangskonto für Antworten',
      responseAccountHint: 'Wähle oder tippe die E-Mail-Adresse ein, auf der du Antworten erhältst.',
      quickShortcuts: 'Dein Konto:',
      secTimeline: '3. Termine & Vergütung',
      appliedOn: 'Beworben am',
      nextStepOn: 'Vorstellungsgespräch / Nächster Termin',
      salary: 'Gehaltsvorstellung / Angebot',
      salaryPlaceholder: 'z. B. 55.000 € / Jahr',
      secDetails: '4. Stellenlink & Notizen',
      link: 'Link zur Stellenanzeige (URL)',
      notes: 'Notizen, Fachfragen, Gesprächsdetails',
      notesPlaceholder: 'Ansprechpartner, Fragen fürs Vorstellungsgespräch, Lebenslauf-Version...',
      cancel: 'Abbrechen',
      delete: 'Löschen',
      save: 'Bewerbung speichern',
      update: 'Aktualisieren',
      saving: 'Wird gespeichert...',
    },
    deleteModal: {
      title: 'Löschen bestätigen',
      message: 'Möchtest du diese Bewerbung wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.',
      cancel: 'Abbrechen',
      confirm: 'Endgültig löschen',
    },
  },
};

const inputCls =
  'h-11 w-full rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 px-3.5 text-xs sm:text-sm text-zinc-950 dark:text-zinc-50 placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-white focus:outline-none transition';

const labelCls =
  'mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-zinc-700 dark:text-zinc-300';

export default function JobApplicationsPage() {
  const { user, loading: authLoading } = useAuth();
  const { language } = useLanguage();

  const isDe = language === 'de';
  const t = T[isDe ? 'de' : 'sq'];

  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [viewMode, setViewMode] = useState('cards');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    company: '',
    role: '',
    platform: 'LinkedIn',
    status: 'applied',
    applied_on: new Date().toISOString().split('T')[0],
    next_step_on: '',
    salary: '',
    link: '',
    notes: '',
    email_subject: '',
    contact_email: '',
    response_email_account: user?.email || '',
  });

  const emailShortcuts = useMemo(
    () => getUserEmailShortcuts(user, applications),
    [user, applications]
  );

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchJobApplications();
      setApplications(data);
    } catch (err) {
      console.error('Error fetching job applications:', err);
      setError(t.errorLoading);
    } finally {
      setLoading(false);
    }
  }, [t.errorLoading]);

  useEffect(() => {
    if (user && canUseJobTracker(user)) {
      loadApplications();
    }
  }, [user, loadApplications]);

  const openNewModal = () => {
    setEditingApp(null);
    setFormData({
      company: '',
      role: '',
      platform: 'LinkedIn',
      status: 'applied',
      applied_on: new Date().toISOString().split('T')[0],
      next_step_on: '',
      salary: '',
      link: '',
      notes: '',
      email_subject: '',
      contact_email: '',
      response_email_account: user?.email || '',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (app) => {
    setEditingApp(app);
    setFormData({
      company: app.company || '',
      role: app.role || '',
      platform: app.platform || 'LinkedIn',
      status: app.status || 'applied',
      applied_on: app.applied_on || '',
      next_step_on: app.next_step_on || '',
      salary: app.salary || '',
      link: app.link || '',
      notes: app.notes || '',
      email_subject: app.email_subject || '',
      contact_email: app.contact_email || '',
      response_email_account: app.response_email_account || user?.email || '',
    });
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company.trim()) return;

    try {
      setSubmitting(true);
      const payload = {
        company: formData.company.trim(),
        role: formData.role.trim() || null,
        platform: formData.platform || null,
        status: formData.status || 'applied',
        applied_on: formData.applied_on || null,
        next_step_on: formData.next_step_on || null,
        salary: formData.salary.trim() || null,
        link: formData.link.trim() || null,
        notes: formData.notes.trim() || null,
        email_subject: formData.email_subject.trim() || null,
        contact_email: formData.contact_email.trim() || null,
        response_email_account: formData.response_email_account.trim() || null,
      };

      if (editingApp) {
        const updated = await updateJobApplication(editingApp.id, payload);
        setApplications((prev) =>
          prev.map((item) => (item.id === editingApp.id ? { ...item, ...updated } : item))
        );
      } else {
        const created = await createJobApplication(payload);
        setApplications((prev) => [created, ...prev]);
      }

      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving application:', err);
      alert((isDe ? 'Fehler beim Speichern: ' : 'Gabim gjatë ruajtjes: ') + (err.message || ''));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteJobApplication(id);
      setApplications((prev) => prev.filter((item) => item.id !== id));
      setDeleteConfirmId(null);
    } catch (err) {
      console.error('Error deleting application:', err);
      alert(isDe ? 'Fehler beim Löschen.' : 'Gabim gjatë fshirjes.');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateJobStatus(id, newStatus);
      setApplications((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status: newStatus } : item))
      );
    } catch (err) {
      console.error('Error updating status:', err);
      alert(isDe ? 'Fehler beim Aktualisieren.' : 'Gabim gjatë përditësimit.');
    }
  };

  const filteredApps = useMemo(() => {
    return applications.filter((app) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        app.company?.toLowerCase().includes(q) ||
        app.role?.toLowerCase().includes(q) ||
        app.email_subject?.toLowerCase().includes(q) ||
        app.contact_email?.toLowerCase().includes(q) ||
        app.response_email_account?.toLowerCase().includes(q) ||
        app.notes?.toLowerCase().includes(q);

      const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
      const matchesPlatform = platformFilter === 'all' || app.platform === platformFilter;

      return matchesSearch && matchesStatus && matchesPlatform;
    });
  }, [applications, searchQuery, statusFilter, platformFilter]);

  const stats = useMemo(() => {
    const total = applications.length;
    const active = applications.filter((a) =>
      ['applied', 'test', 'interview', 'follow_up'].includes(a.status)
    ).length;
    const noResponse = applications.filter((a) => a.status === 'no_response').length;
    const interviews = applications.filter((a) => a.status === 'interview').length;
    const offers = applications.filter((a) => a.status === 'offer').length;
    const rejected = applications.filter((a) => ['rejected', 'declined'].includes(a.status)).length;
    return { total, active, noResponse, interviews, offers, rejected };
  }, [applications]);

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || platformFilter !== 'all';

  const clearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPlatformFilter('all');
  };

  if (!authLoading && !canUseJobTracker(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  const statItems = [
    {
      key: 'total',
      label: t.stats.total,
      value: stats.total,
      sub: t.stats.totalSub,
      icon: Briefcase,
      dot: null,
    },
    {
      key: 'active',
      label: t.stats.active,
      value: stats.active,
      sub: t.stats.activeSub,
      icon: Clock,
      dot: 'sky',
      pulse: false,
    },
    {
      key: 'noResponse',
      label: t.stats.noResponse,
      value: stats.noResponse,
      sub: t.stats.noResponseSub,
      icon: HelpCircle,
      dot: 'amber',
      pulse: false,
    },
    {
      key: 'interviews',
      label: t.stats.interviews,
      value: stats.interviews,
      sub: t.stats.interviewsSub,
      icon: Sparkles,
      dot: 'amber',
      pulse: true,
    },
    {
      key: 'offers',
      label: t.stats.offers,
      value: stats.offers,
      sub: t.stats.offersSub,
      icon: CheckCircle2,
      dot: 'emerald',
      pulse: true,
    },
    {
      key: 'closed',
      label: t.stats.closed,
      value: stats.rejected,
      sub: t.stats.closedSub,
      icon: Archive,
      dot: 'rose',
      pulse: false,
    },
  ];

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-[1400px] space-y-6 pb-16 font-sans">
        {/* ─── 1. LOGIN-STYLE BLACK SHOWCASE HERO ─── */}
        <GermanHero
          eyebrow={t.eyebrow}
          title={
            <>
              {t.titleLine1}
              <br />
              <span className="text-zinc-400">{t.titleLine2}</span>
            </>
          }
          subtitle={t.subtitle}
          actions={
            <>
              <button
                type="button"
                onClick={() =>
                  exportApplicationsToExcel(
                    filteredApps.length > 0 ? filteredApps : applications,
                    language
                  )
                }
                className={`${heroGhostBtn} cursor-pointer`}
                title={t.exportExcel}
              >
                <FileSpreadsheet size={15} className="text-[#FFCE00]" />
                <span>{t.exportExcel}</span>
                <span className="rounded-md bg-white/15 px-1.5 py-0.5 text-[10px] font-semibold">
                  {filteredApps.length}
                </span>
              </button>

              <button
                type="button"
                onClick={openNewModal}
                className={`${heroPrimaryBtn} cursor-pointer`}
              >
                <Plus size={15} />
                <span>{t.newApp}</span>
              </button>
            </>
          }
        >
          {/* Embedded German Bewerbungswortschatz B2 Strip */}
          <div className="border-t border-white/10 pt-5">
            <div className="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
              <BookOpen size={13} className="text-[#FFCE00]" />
              <span>{t.glossarTitle}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
              {BEWERBUNGS_GLOSSAR.map((term) => (
                <div
                  key={term.de}
                  className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2"
                >
                  <p className="truncate text-xs font-semibold text-white">{term.de}</p>
                  <p className="truncate text-[11px] text-zinc-400">{term.sq}</p>
                </div>
              ))}
            </div>
          </div>
        </GermanHero>

        {/* ─── 2. MINIMAL WHITE KPI CARDS (6) ─── */}
        <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-6">
          {statItems.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.key}
                className={`${surfaceCard} p-4 transition-colors hover:border-zinc-300 dark:hover:border-white/20`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="flex items-center gap-1.5 truncate text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                    {item.dot && <StatusIndicator color={item.dot} pulse={item.pulse} />}
                    <span className="truncate">{item.label}</span>
                  </span>
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-zinc-200 dark:border-white/10 text-zinc-700 dark:text-zinc-200">
                    <Icon size={14} />
                  </div>
                </div>
                <div className="mt-3 flex items-baseline justify-between">
                  <span className="text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 tabular-nums">
                    {item.value}
                  </span>
                </div>
                <p className="mt-1 truncate text-[11px] text-zinc-400 dark:text-zinc-500">
                  {item.sub}
                </p>
              </div>
            );
          })}
        </div>

        {/* ─── 3. UNIFIED FILTER & VIEW TOOLBAR ─── */}
        <div className={`${surfaceCard} p-3 sm:p-4`}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search
                size={15}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className="h-11 w-full rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 pl-10 pr-9 text-xs sm:text-sm text-zinc-950 dark:text-zinc-50 placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-white focus:outline-none transition"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Filters & View Switches */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-11 appearance-none rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 pl-3.5 pr-8 text-xs font-semibold text-zinc-800 dark:text-zinc-200 focus:border-zinc-900 focus:outline-none cursor-pointer"
                >
                  <option value="all">{t.allStatuses}</option>
                  {JOB_STATUSES.map((st) => (
                    <option key={st.id} value={st.id}>
                      {isDe ? st.labelDe : `${st.labelDe} · ${st.labelSq}`}
                    </option>
                  ))}
                </select>
                <Filter
                  size={12}
                  className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400"
                />
              </div>

              <div className="relative">
                <select
                  value={platformFilter}
                  onChange={(e) => setPlatformFilter(e.target.value)}
                  className="h-11 appearance-none rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 pl-3.5 pr-8 text-xs font-semibold text-zinc-800 dark:text-zinc-200 focus:border-zinc-900 focus:outline-none cursor-pointer"
                >
                  <option value="all">{t.allPlatforms}</option>
                  {JOB_PLATFORMS.map((p) => (
                    <option key={p} value={p}>
                      {getPlatformLabel(p, language)}
                    </option>
                  ))}
                </select>
                <Filter
                  size={12}
                  className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400"
                />
              </div>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="h-11 rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 px-3.5 text-xs font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition cursor-pointer"
                >
                  {t.clearFilters}
                </button>
              )}

              {/* View Mode Toggle */}
              <div className="flex h-11 items-center rounded-xl border border-zinc-200 dark:border-white/10 p-1">
                {[
                  { id: 'cards', icon: LayoutGrid, label: t.viewCards },
                  { id: 'list', icon: LayoutList, label: t.viewList },
                  { id: 'kanban', icon: Kanban, label: t.viewKanban },
                ].map(({ id, icon: Icon, label }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setViewMode(id)}
                    className={`flex h-full items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold transition cursor-pointer ${
                      viewMode === id
                        ? 'bg-black text-white dark:bg-white dark:text-black'
                        : 'text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100'
                    }`}
                    title={label}
                  >
                    <Icon size={14} />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ─── 4. MAIN CONTENT AREA ─── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-black dark:border-white border-t-transparent" />
            <p className="mt-4 text-xs font-semibold text-zinc-500">{t.loading}</p>
          </div>
        ) : error ? (
          <div className={`${surfaceCard} p-8 text-center`}>
            <AlertCircle size={26} className="mx-auto mb-3 text-[#DD0000]" />
            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{error}</p>
            <button
              type="button"
              onClick={loadApplications}
              className="mt-4 rounded-xl bg-black px-5 py-2.5 text-xs font-semibold text-white hover:bg-zinc-800 transition cursor-pointer"
            >
              {t.retry}
            </button>
          </div>
        ) : filteredApps.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-zinc-300 dark:border-white/15 bg-white dark:bg-zinc-900 py-20 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-black text-white dark:bg-white dark:text-black">
              <Building2 size={22} />
            </div>
            <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
              {t.noAppsFound}
            </h3>
            <p className="mx-auto mt-1 max-w-md text-xs text-zinc-500">
              {hasActiveFilters ? t.noAppsDescFilter : t.noAppsDescEmpty}
            </p>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearAllFilters}
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-black px-4 py-2.5 text-xs font-semibold text-white hover:bg-zinc-800 dark:bg-white dark:text-black cursor-pointer"
              >
                <span>{t.clearFilters}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={openNewModal}
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-black px-4 py-2.5 text-xs font-semibold text-white hover:bg-zinc-800 dark:bg-white dark:text-black cursor-pointer"
              >
                <Plus size={14} />
                <span>{t.addFirstApp}</span>
              </button>
            )}
          </div>
        ) : viewMode === 'cards' ? (
          /* ─── 4A. CARD GRID VIEW ─── */
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredApps.map((app) => {
              const statusMeta = STATUS_MAP[app.status] || STATUS_MAP.applied;
              const platformLabel = getPlatformLabel(app.platform, language);

              return (
                <div
                  key={app.id}
                  className={`${surfaceCard} flex flex-col justify-between p-5 transition hover:border-zinc-900 dark:hover:border-white/40`}
                >
                  <div className="space-y-3.5">
                    {/* Top Row: Company & Status */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-base font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                          {app.company}
                        </h3>
                        {app.role ? (
                          <p className="mt-0.5 flex items-center gap-1.5 line-clamp-1 text-xs text-zinc-600 dark:text-zinc-400">
                            <Briefcase size={12} className="shrink-0 text-zinc-400" />
                            <span>{app.role}</span>
                          </p>
                        ) : (
                          <p className="mt-0.5 text-xs italic text-zinc-400">
                            {isDe ? 'Keine Position angegeben' : 'Pa pozitë të specifikuar'}
                          </p>
                        )}
                      </div>

                      <span
                        className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${statusMeta.pill}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${statusMeta.dot}`} />
                        {isDe ? statusMeta.labelDe : statusMeta.labelSq}
                      </span>
                    </div>

                    {/* Email Nachweis Box */}
                    {app.email_subject || app.response_email_account || app.contact_email ? (
                      <div className="space-y-2 rounded-xl border border-zinc-200 dark:border-white/10 bg-zinc-50/70 dark:bg-zinc-800/40 p-3 text-xs">
                        {app.email_subject && (
                          <div className="space-y-1">
                            <div className="flex items-center justify-between gap-1">
                              <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                                <Mail size={11} />
                                {t.card.emailSubject}
                              </span>
                              <CopyButton text={app.email_subject} size="xs" />
                            </div>
                            <p className="line-clamp-2 break-words rounded-lg border border-zinc-200/80 dark:border-white/10 bg-white dark:bg-zinc-900 p-1.5 font-mono text-[11px] text-zinc-800 dark:text-zinc-200 select-all">
                              "{app.email_subject}"
                            </p>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                          {app.response_email_account && (
                            <span className="inline-flex items-center gap-1 rounded-md border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 px-2 py-0.5 text-[10.5px] font-medium text-zinc-700 dark:text-zinc-300">
                              <Inbox size={11} className="shrink-0 text-emerald-600" />
                              <span>{t.card.responseAccount}:</span>
                              <span className="font-mono">{app.response_email_account}</span>
                            </span>
                          )}

                          {app.contact_email && (
                            <span className="truncate text-[10.5px] text-zinc-500">
                              {t.card.sender}:{' '}
                              <span className="font-mono text-zinc-700 dark:text-zinc-300">
                                {app.contact_email}
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 rounded-xl border border-zinc-200/70 dark:border-white/10 px-3 py-2 text-[11px] text-zinc-400">
                        <Mail size={12} className="shrink-0" />
                        <span>{t.card.noEmailLinked}</span>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="flex flex-wrap items-center gap-1.5 text-xs">
                      {platformLabel && (
                        <span className="inline-flex items-center rounded-lg border border-zinc-200 dark:border-white/10 px-2 py-0.5 text-[11px] font-medium text-zinc-700 dark:text-zinc-300">
                          {platformLabel}
                        </span>
                      )}

                      {app.applied_on && (
                        <span className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 dark:border-white/10 px-2 py-0.5 text-[11px] text-zinc-500">
                          <Calendar size={11} />
                          <span>{app.applied_on}</span>
                        </span>
                      )}

                      {app.next_step_on && (
                        <span className="inline-flex items-center gap-1 rounded-lg bg-[#FFCE00]/20 px-2 py-0.5 text-[11px] font-semibold text-zinc-900 dark:text-[#FFCE00]">
                          <Clock size={11} />
                          <span>{app.next_step_on}</span>
                        </span>
                      )}

                      {app.salary && (
                        <span className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 dark:text-emerald-300">
                          <DollarSign size={11} />
                          <span>{app.salary}</span>
                        </span>
                      )}
                    </div>

                    {app.notes && (
                      <p className="line-clamp-2 rounded-xl border border-zinc-200/70 dark:border-white/10 p-2.5 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400">
                        {app.notes}
                      </p>
                    )}
                  </div>

                  {/* Card Footer */}
                  <div className="mt-4 flex items-center justify-between gap-2 border-t border-zinc-200 dark:border-white/10 pt-3.5">
                    <select
                      value={app.status}
                      onChange={(e) => handleStatusChange(app.id, e.target.value)}
                      className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 px-2.5 py-1.5 text-xs font-semibold text-zinc-800 dark:text-zinc-200 focus:border-zinc-900 focus:outline-none cursor-pointer"
                    >
                      {JOB_STATUSES.map((st) => (
                        <option key={st.id} value={st.id}>
                          {isDe ? st.labelDe : st.labelSq}
                        </option>
                      ))}
                    </select>

                    <div className="flex items-center gap-1.5">
                      {app.link && (
                        <a
                          href={app.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-xl border border-zinc-200 dark:border-white/10 p-1.5 text-zinc-500 hover:border-zinc-900 hover:text-zinc-950 dark:hover:text-white transition"
                          title={t.card.openLink}
                        >
                          <ExternalLink size={13} />
                        </a>
                      )}

                      <button
                        type="button"
                        onClick={() => openEditModal(app)}
                        className="rounded-xl border border-zinc-200 dark:border-white/10 p-1.5 text-zinc-600 hover:border-zinc-900 hover:text-zinc-950 dark:text-zinc-300 dark:hover:text-white transition cursor-pointer"
                        title={t.card.edit}
                      >
                        <Pencil size={13} />
                      </button>

                      <button
                        type="button"
                        onClick={() => setDeleteConfirmId(app.id)}
                        className="rounded-xl border border-zinc-200 dark:border-white/10 p-1.5 text-zinc-400 hover:border-[#DD0000] hover:text-[#DD0000] transition cursor-pointer"
                        title={t.card.delete}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : viewMode === 'list' ? (
          /* ─── 4B. LIST VIEW ─── */
          <div className="space-y-2.5">
            {filteredApps.map((app) => {
              const statusMeta = STATUS_MAP[app.status] || STATUS_MAP.applied;
              const platformLabel = getPlatformLabel(app.platform, language);

              return (
                <div
                  key={app.id}
                  className={`${surfaceCard} flex flex-col gap-4 p-4 transition hover:border-zinc-900 dark:hover:border-white/40 lg:flex-row lg:items-center lg:justify-between`}
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">
                        {app.company}
                      </h4>
                      {platformLabel && (
                        <span className="rounded-md border border-zinc-200 dark:border-white/10 px-2 py-0.5 text-[11px] font-medium text-zinc-600 dark:text-zinc-300">
                          {platformLabel}
                        </span>
                      )}
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${statusMeta.pill}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${statusMeta.dot}`} />
                        {isDe ? statusMeta.labelDe : statusMeta.labelSq}
                      </span>
                    </div>

                    {app.role && <p className="text-xs text-zinc-500">{app.role}</p>}

                    {app.email_subject && (
                      <p className="max-w-xl truncate font-mono text-[11px] text-zinc-600 dark:text-zinc-400">
                        "{app.email_subject}"
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
                    {app.response_email_account && (
                      <span className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-white/10 px-2.5 py-1 font-mono text-[11px] text-zinc-700 dark:text-zinc-300">
                        <Inbox size={12} className="text-emerald-600" />
                        <span>{app.response_email_account}</span>
                      </span>
                    )}

                    {app.applied_on && (
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        <span>{app.applied_on}</span>
                      </span>
                    )}

                    {app.next_step_on && (
                      <span className="flex items-center gap-1 rounded-md bg-[#FFCE00]/20 px-2 py-0.5 font-semibold text-zinc-900 dark:text-[#FFCE00]">
                        <Clock size={12} />
                        <span>{app.next_step_on}</span>
                      </span>
                    )}

                    {app.salary && (
                      <span className="flex items-center gap-1 font-semibold text-emerald-700 dark:text-emerald-400">
                        <DollarSign size={12} />
                        <span>{app.salary}</span>
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 self-end lg:self-center">
                    <select
                      value={app.status}
                      onChange={(e) => handleStatusChange(app.id, e.target.value)}
                      className="rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 px-2.5 py-1.5 text-xs font-semibold text-zinc-800 dark:text-zinc-200 focus:border-zinc-900 focus:outline-none cursor-pointer"
                    >
                      {JOB_STATUSES.map((st) => (
                        <option key={st.id} value={st.id}>
                          {isDe ? st.labelDe : st.labelSq}
                        </option>
                      ))}
                    </select>

                    {app.link && (
                      <a
                        href={app.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-xl border border-zinc-200 dark:border-white/10 p-2 text-zinc-500 hover:border-zinc-900 hover:text-zinc-950"
                        title={t.card.openLink}
                      >
                        <ExternalLink size={14} />
                      </a>
                    )}

                    <button
                      type="button"
                      onClick={() => openEditModal(app)}
                      className="rounded-xl border border-zinc-200 dark:border-white/10 p-2 text-zinc-600 hover:border-zinc-900 hover:text-zinc-950 cursor-pointer"
                      title={t.card.edit}
                    >
                      <Pencil size={14} />
                    </button>

                    <button
                      type="button"
                      onClick={() => setDeleteConfirmId(app.id)}
                      className="rounded-xl border border-zinc-200 dark:border-white/10 p-2 text-zinc-400 hover:border-[#DD0000] hover:text-[#DD0000] cursor-pointer"
                      title={t.card.delete}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ─── 4C. KANBAN BOARD VIEW ─── */
          <div className="flex gap-4 overflow-x-auto pb-6">
            {JOB_STATUSES.map((column) => {
              const colApps = filteredApps.filter((a) => a.status === column.id);

              return (
                <div
                  key={column.id}
                  className={`${surfaceCard} flex w-[280px] min-w-[280px] shrink-0 flex-col p-3.5`}
                >
                  <div className="mb-3 flex items-center justify-between border-b border-zinc-200 dark:border-white/10 pb-2.5">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${column.dot}`} />
                      <h4 className="text-xs font-semibold text-zinc-900 dark:text-zinc-100">
                        {isDe ? column.labelDe : column.labelSq}
                      </h4>
                    </div>
                    <span className="rounded-md bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 text-[10.5px] font-semibold text-zinc-700 dark:text-zinc-300">
                      {colApps.length}
                    </span>
                  </div>

                  <div className="max-h-[680px] flex-1 space-y-2.5 overflow-y-auto pr-1">
                    {colApps.map((app) => (
                      <div
                        key={app.id}
                        className="space-y-2 rounded-xl border border-zinc-200 dark:border-white/10 bg-zinc-50/60 dark:bg-zinc-800/40 p-3 transition hover:border-zinc-900 dark:hover:border-white/40"
                      >
                        <div className="flex items-start justify-between gap-1.5">
                          <h5 className="text-xs font-semibold text-zinc-950 dark:text-zinc-50">
                            {app.company}
                          </h5>
                          <button
                            type="button"
                            onClick={() => openEditModal(app)}
                            className="p-0.5 text-zinc-400 hover:text-zinc-900 dark:hover:text-white cursor-pointer"
                            title={t.card.edit}
                          >
                            <Pencil size={12} />
                          </button>
                        </div>

                        {app.role && (
                          <p className="line-clamp-1 text-[11px] text-zinc-500">{app.role}</p>
                        )}

                        {app.email_subject && (
                          <div className="line-clamp-2 rounded-lg border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 p-1.5 font-mono text-[10px] text-zinc-700 dark:text-zinc-300">
                            "{app.email_subject}"
                          </div>
                        )}

                        {app.response_email_account && (
                          <div
                            className="flex items-center gap-1 truncate font-mono text-[10px] text-zinc-500"
                            title={app.response_email_account}
                          >
                            <Inbox size={10} className="shrink-0 text-emerald-600" />
                            <span className="truncate">{app.response_email_account}</span>
                          </div>
                        )}

                        <div className="flex items-center justify-between border-t border-zinc-200/70 dark:border-white/10 pt-1.5 text-[10px] text-zinc-400">
                          <span>{getPlatformLabel(app.platform, language) || 'E-Mail'}</span>
                          {app.applied_on && <span>{app.applied_on}</span>}
                        </div>
                      </div>
                    ))}

                    {colApps.length === 0 && (
                      <div className="py-8 text-center text-[11px] text-zinc-400">
                        {t.card.emptyColumn}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ─── 5. CREATE / EDIT APPLICATION DIALOG (LOGIN FORM STYLE) ─── */}
        <Dialog.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs" />
            <Dialog.Content
              className={cn(
                'fixed z-50 flex flex-col overflow-hidden bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 shadow-2xl outline-none',
                'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-3xl',
                'sm:inset-auto sm:left-1/2 sm:top-1/2 sm:max-h-[88vh] sm:w-[calc(100%-2rem)] sm:max-w-2xl sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-3xl'
              )}
            >
              {/* Black Header matching login showcase */}
              <div className="relative flex shrink-0 items-center justify-between bg-black px-6 py-5 text-white">
                <div className="min-w-0 space-y-1.5">
                  <FlagBar className="h-1 w-16" />
                  <Dialog.Title className="truncate text-lg font-semibold tracking-tight text-white">
                    {editingApp ? t.modal.editTitle : t.modal.createTitle}
                  </Dialog.Title>
                  <Dialog.Description className="truncate text-xs text-zinc-400">
                    {t.modal.subtitle}
                  </Dialog.Description>
                </div>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/15 text-zinc-300 hover:bg-white/10 hover:text-white transition cursor-pointer"
                    aria-label="Mbyll"
                  >
                    <X size={16} />
                  </button>
                </Dialog.Close>
              </div>

              <form
                onSubmit={handleFormSubmit}
                className="flex min-h-0 flex-1 flex-col overflow-hidden"
              >
                <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-6 py-6">
                  {/* Section 1 */}
                  <div className="space-y-4">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                      {t.modal.secJob}
                    </h4>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className={labelCls}>
                          {t.modal.company} <span className="text-[#DD0000]">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          placeholder={t.modal.companyPlaceholder}
                          className={inputCls}
                        />
                      </div>

                      <div>
                        <label className={labelCls}>{t.modal.role}</label>
                        <input
                          type="text"
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                          placeholder={t.modal.rolePlaceholder}
                          className={inputCls}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className={labelCls}>{t.modal.status}</label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          className={`${inputCls} cursor-pointer`}
                        >
                          {JOB_STATUSES.map((st) => (
                            <option key={st.id} value={st.id}>
                              {isDe ? st.labelDe : `${st.labelDe} · ${st.labelSq}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className={labelCls}>{t.modal.platform}</label>
                        <select
                          value={formData.platform}
                          onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                          className={`${inputCls} cursor-pointer`}
                        >
                          {JOB_PLATFORMS.map((p) => (
                            <option key={p} value={p}>
                              {getPlatformLabel(p, language)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Email Intelligence */}
                  <div className="space-y-4 rounded-2xl border border-zinc-200 dark:border-white/10 p-4">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                      {t.modal.secEmail}
                    </h4>

                    <div>
                      <div className="mb-1.5 flex items-center justify-between">
                        <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-700 dark:text-zinc-300">
                          {t.modal.emailSubject}
                        </label>
                        {formData.email_subject && (
                          <CopyButton text={formData.email_subject} size="xs" />
                        )}
                      </div>
                      <input
                        type="text"
                        value={formData.email_subject}
                        onChange={(e) =>
                          setFormData({ ...formData, email_subject: e.target.value })
                        }
                        placeholder={t.modal.emailSubjectPlaceholder}
                        className={`${inputCls} font-mono`}
                      />
                      <p className="mt-1 text-[11px] text-zinc-400">{t.modal.emailSubjectHint}</p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className={labelCls}>{t.modal.contactEmail}</label>
                        <input
                          type="email"
                          value={formData.contact_email}
                          onChange={(e) =>
                            setFormData({ ...formData, contact_email: e.target.value })
                          }
                          placeholder={t.modal.contactEmailPlaceholder}
                          className={inputCls}
                        />
                      </div>

                      <div>
                        <label className={labelCls}>{t.modal.responseAccount}</label>
                        <input
                          type="email"
                          value={formData.response_email_account}
                          onChange={(e) =>
                            setFormData({ ...formData, response_email_account: e.target.value })
                          }
                          placeholder={user?.email || 'email@example.com'}
                          className={`${inputCls} font-mono`}
                        />
                      </div>
                    </div>

                    {emailShortcuts.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        <span className="text-[11px] text-zinc-400">{t.modal.quickShortcuts}</span>
                        {emailShortcuts.map((em) => (
                          <button
                            key={em}
                            type="button"
                            onClick={() =>
                              setFormData({ ...formData, response_email_account: em })
                            }
                            className={cn(
                              'rounded-lg px-2.5 py-1 font-mono text-[11px] transition cursor-pointer',
                              formData.response_email_account === em
                                ? 'bg-black text-white dark:bg-white dark:text-black font-semibold'
                                : 'border border-zinc-200 dark:border-white/10 text-zinc-600 dark:text-zinc-300 hover:border-zinc-900'
                            )}
                          >
                            {em}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Section 3: Timeline */}
                  <div className="space-y-4">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                      {t.modal.secTimeline}
                    </h4>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div>
                        <label className={labelCls}>{t.modal.appliedOn}</label>
                        <input
                          type="date"
                          value={formData.applied_on}
                          onChange={(e) =>
                            setFormData({ ...formData, applied_on: e.target.value })
                          }
                          className={inputCls}
                        />
                      </div>

                      <div>
                        <label className={labelCls}>{t.modal.nextStepOn}</label>
                        <input
                          type="date"
                          value={formData.next_step_on}
                          onChange={(e) =>
                            setFormData({ ...formData, next_step_on: e.target.value })
                          }
                          className={inputCls}
                        />
                      </div>

                      <div>
                        <label className={labelCls}>{t.modal.salary}</label>
                        <input
                          type="text"
                          value={formData.salary}
                          onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                          placeholder={t.modal.salaryPlaceholder}
                          className={inputCls}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Link & Notes */}
                  <div className="space-y-4">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                      {t.modal.secDetails}
                    </h4>

                    <div>
                      <label className={labelCls}>{t.modal.link}</label>
                      <input
                        type="url"
                        value={formData.link}
                        onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                        placeholder="https://..."
                        className={inputCls}
                      />
                    </div>

                    <div>
                      <label className={labelCls}>{t.modal.notes}</label>
                      <textarea
                        rows={3}
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder={t.modal.notesPlaceholder}
                        className="w-full rounded-xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 p-3.5 text-xs sm:text-sm text-zinc-950 dark:text-zinc-50 placeholder:text-zinc-400 focus:border-zinc-900 dark:focus:border-white focus:outline-none transition"
                      />
                    </div>
                  </div>
                </div>

                {/* Sticky Footer with Login-style Solid Black CTA */}
                <div className="flex shrink-0 items-center justify-between gap-3 border-t border-zinc-200 dark:border-white/10 bg-white dark:bg-zinc-900 px-6 py-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="h-11 rounded-xl border border-zinc-200 dark:border-white/10 px-4 text-xs sm:text-sm font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition cursor-pointer"
                  >
                    {t.modal.cancel}
                  </button>
                  <div className="flex items-center gap-2">
                    {editingApp && (
                      <button
                        type="button"
                        onClick={() => {
                          const target = editingApp;
                          setIsModalOpen(false);
                          setDeleteConfirmId(target.id);
                        }}
                        className="inline-flex h-11 items-center gap-1.5 rounded-xl px-3.5 text-xs sm:text-sm font-semibold text-[#DD0000] hover:bg-red-50 dark:hover:bg-red-950/30 transition cursor-pointer"
                      >
                        <Trash2 size={15} />
                        <span>{t.modal.delete}</span>
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex h-11 items-center gap-2 rounded-xl bg-black px-6 text-xs sm:text-sm font-semibold text-white hover:bg-zinc-800 dark:bg-white dark:text-black dark:hover:bg-zinc-200 transition cursor-pointer disabled:opacity-50"
                    >
                      <Check size={15} />
                      <span>
                        {submitting
                          ? t.modal.saving
                          : editingApp
                            ? t.modal.update
                            : t.modal.save}
                      </span>
                    </button>
                  </div>
                </div>
              </form>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* ─── 6. DELETE CONFIRMATION DIALOG ─── */}
        <Dialog.Root
          open={Boolean(deleteConfirmId)}
          onOpenChange={(open) => !open && setDeleteConfirmId(null)}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs" />
            <Dialog.Content
              className={cn(
                'fixed z-50 flex flex-col overflow-hidden bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 shadow-2xl outline-none',
                'inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto rounded-3xl'
              )}
            >
              <div className="p-6 space-y-5">
                <div className="flex items-start gap-3.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#DD0000] text-white">
                    <AlertCircle size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Dialog.Title className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
                      {t.deleteModal.title}
                    </Dialog.Title>
                    <Dialog.Description className="mt-1 text-xs leading-relaxed text-zinc-500">
                      {t.deleteModal.message}
                    </Dialog.Description>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2.5 border-t border-zinc-200 dark:border-white/10 pt-4">
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmId(null)}
                    className="h-10 rounded-xl border border-zinc-200 dark:border-white/10 px-4 text-xs font-semibold text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 transition cursor-pointer"
                  >
                    {t.deleteModal.cancel}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(deleteConfirmId)}
                    className="h-10 rounded-xl bg-[#DD0000] px-5 text-xs font-semibold text-white hover:bg-[#b80000] transition cursor-pointer"
                  >
                    {t.deleteModal.confirm}
                  </button>
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </DashboardLayout>
  );
}
